import React from 'react';

// Utils
import { SCHEMA_TYPE_MULTI_ENUM, SCHEMA_TYPE_TEXT, SCHEMA_TYPE_YOUTUBE } from '../../util/types';
import {
  isFieldForCategory,
  pickCategoryFields,
  pickCustomFieldProps,
} from '../../util/fieldHelpers.js';
import { LISTING_TYPES } from '../../util/listingTypes';

import SectionDetailsMaybe from './SectionDetailsMaybe';
import SectionMultiEnumMaybe from './SectionMultiEnumMaybe';
import SectionPriceVerificationMaybe from './SectionPriceVerificationMaybe';
import SectionTextMaybe from './SectionTextMaybe';
import SectionYoutubeVideoMaybe from './SectionYoutubeVideoMaybe';

/**
 * Renders custom listing fields.
 * - SectionDetailsMaybe is used if schemaType is 'enum', 'long', or 'boolean'
 * - SectionMultiEnumMaybe is used if schemaType is 'multi-enum'
 * - SectionTextMaybe is used if schemaType is 'text'
 *
 * @param {*} props include publicData, metadata, listingFieldConfigs, categoryConfiguration
 * @returns React.Fragment containing aforementioned components
 */
const CustomListingFields = props => {
  const { publicData, metadata, listingFieldConfigs, categoryConfiguration, intl } = props;

  // Check if this is a bid listing - if so, hide SectionDetailsMaybe
  const listingType = publicData?.listingType;
  const isBidListing = listingType === LISTING_TYPES.TICKET_REQUEST;
  const isTicketSale = listingType === 'buying-tickets';

  const { key: categoryPrefix, categories: listingCategoriesConfig } = categoryConfiguration;
  const categoriesObj = pickCategoryFields(publicData, categoryPrefix, 1, listingCategoriesConfig);
  const currentCategories = Object.values(categoriesObj);

  const isFieldForSelectedCategories = fieldConfig => {
    const isTargetCategory = isFieldForCategory(currentCategories, fieldConfig);
    return isTargetCategory;
  };
  const propsForCustomFields =
    pickCustomFieldProps(
      publicData,
      metadata,
      listingFieldConfigs,
      'listingType',
      isFieldForSelectedCategories
    ) || [];

  const adjustedPropsForCustomFields = propsForCustomFields
    .map(elm => {
      const { key } = elm;

      // For bid listings, exclude venueName, venue, ticketType, and message since they're shown in Bid Details section
      // venueName is duplicate of venue, so exclude both to avoid duplication
      // message is shown in SectionBidDetailsMaybe as "Buyer's message", so exclude it here to avoid duplication
      if (
        isBidListing &&
        (key === 'venueName' ||
          key === 'venue' ||
          key === 'ticketType' ||
          key === 'ticket_type' ||
          key === 'message')
      ) {
        return null;
      }

      if (key === 'eventDateTime') {
        const date = new Date(elm.text);
        const formattedDate = date.toLocaleDateString('en-US', {
          month: 'long',
          day: 'numeric',
          year: 'numeric',
        });
        const readableTime = date.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: 'numeric',
          hour12: true,
        });

        return {
          ...elm,
          text: `${formattedDate} ${readableTime}`,
        };
      } else if (key === 'seatSection') {
        const seatRow = propsForCustomFields.find(field => field.key === 'seatRow');
        const seatNumber = propsForCustomFields.find(field => field.key === 'seatNumber');

        // Build seat info only if at least one field has a value
        const seatParts = [];
        if (elm.text) seatParts.push(`Section ${elm.text}`);
        if (seatRow?.text) seatParts.push(`Row ${seatRow.text}`);
        if (seatNumber?.text) seatParts.push(`Seat ${seatNumber.text}`);

        // Only show seat info if at least one part exists
        if (seatParts.length === 0) {
          return null;
        }

        return {
          ...elm,
          heading: 'Seat details',
          text: seatParts.join(', '),
        };
      } else if (key === 'venueName') {
        const venueLocation = propsForCustomFields.find(field => field.key === 'venueLocation');

        // Only show venue if we have venue name
        if (!elm.text) {
          return null;
        }

        return {
          ...elm,
          heading: 'Venue',
          text: venueLocation?.text ? `${elm.text}, ${venueLocation.text}` : elm.text,
        };
      }

      return elm;
    })
    .filter(
      elm => elm && elm.key !== 'seatRow' && elm.key !== 'seatNumber' && elm.key !== 'venueLocation'
    );

  const [element] = adjustedPropsForCustomFields?.splice(1, 1);
  adjustedPropsForCustomFields.push(element);

  // For ticket sales, directly display venue and seat info from publicData
  const venueName = publicData?.venueName;
  const venueLocation = publicData?.venueLocation;
  const seatSection = publicData?.seatSection || publicData?.seat_section;
  const seatRow = publicData?.seatRow || publicData?.seat_row;
  const seatNumber = publicData?.seatNumber || publicData?.seat_number;
  const seatDetails = publicData?.seat_details || publicData?.seatDetails;

  // Build venue display - only show arena name (part before comma)
  const venueText = venueName ? venueName.split(',')[0].trim() : null;

  // Build seat info display (only if at least one field exists)
  const seatParts = [];
  if (seatSection) seatParts.push(`Section ${seatSection}`);
  if (seatRow) seatParts.push(`Row ${seatRow}`);
  if (seatNumber) seatParts.push(`Seat ${seatNumber}`);
  const seatText = seatDetails || (seatParts.length > 0 ? seatParts.join(', ') : null);

  return (
    <>
      {/* Hide Details section for bid listings - all details are shown in Bid Details section */}
      {!isBidListing && (
        <SectionDetailsMaybe {...props} isFieldForCategory={isFieldForSelectedCategories} />
      )}

      <SectionPriceVerificationMaybe publicData={publicData} />

      {/* Display seat info for ticket sales (only if provided) */}
      {isTicketSale && seatText && <SectionTextMaybe heading="Seat details" text={seatText} />}

      {adjustedPropsForCustomFields.map(customFieldProps => {
        // Safety check to prevent crash when customFieldProps is undefined
        if (!customFieldProps) {
          return null;
        }
        const { schemaType, key, ...fieldProps } = customFieldProps;
        return schemaType === SCHEMA_TYPE_MULTI_ENUM ? (
          <SectionMultiEnumMaybe key={key} {...fieldProps} />
        ) : schemaType === SCHEMA_TYPE_TEXT ? (
          <SectionTextMaybe key={key} {...fieldProps} />
        ) : schemaType === SCHEMA_TYPE_YOUTUBE ? (
          <SectionYoutubeVideoMaybe key={key} {...fieldProps} />
        ) : null;
      })}
    </>
  );
};

export default CustomListingFields;
